import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { IDLE_BEFORE_AUTO_UPDATE_MS, resolveAutoUpdateDecision } from '../../domain/pwa/autoUpdatePolicy';
import { applyAppUpdate } from './appUpdate';
import { subscribeAppUpdateStatus } from './appUpdateStatus';

/**
 * Watch for a waiting update and take it at a moment that costs the reader
 * nothing.
 *
 * The three inputs the policy needs — is anyone looking, is anyone doing
 * anything, and have they just gone somewhere — only exist in a mounted
 * component, which is why this is a hook rather than more of `appUpdate.js`. It
 * renders nothing and returns nothing: a reader never learns that any of this
 * happened, which is the point.
 *
 * It is mounted ABOVE the routes, in `AppShell`, so the navigation it watches is
 * every navigation in the app and the watch itself survives all of them.
 */

// `pointermove` is deliberately in the list even though it fires constantly: on
// a desktop, a moving mouse is the difference between a reader and an abandoned
// tab, and all it does here is push a timer back.
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'touchstart', 'wheel', 'scroll'];

function readPageVisibility(documentObject) {
    return !documentObject || documentObject.visibilityState !== 'hidden';
}

/**
 * A sheet or a share chooser opens with `showModal()`, so an open modal is a
 * fact about the document rather than something to thread through React. Asking
 * the DOM keeps the policy's input honest even if a second dialog is added
 * later.
 */
function hasOpenModalDialog(documentObject) {
    return Boolean(documentObject && documentObject.querySelector('dialog[open]'));
}

export function useAutomaticAppUpdate({
    windowObject = typeof window === 'undefined' ? null : window,
    documentObject = typeof document === 'undefined' ? null : document,
    idleAfterMs = IDLE_BEFORE_AUTO_UPDATE_MS,
} = {}) {
    const { pathname } = useLocation();
    const [status, setStatus] = useState(null);
    const [isPageVisible, setIsPageVisible] = useState(() => readPageVisibility(documentObject));
    const [isUserIdle, setIsUserIdle] = useState(false);
    const appliedUpdateKeyRef = useRef(null);

    // The route the shell was on when this mounted. A navigation is any change
    // from it, and the FIRST render is not one — an update applied on arrival
    // would reload the page somebody just opened.
    const arrivedAtRef = useRef(pathname);
    const hasNavigated = pathname !== arrivedAtRef.current;

    useEffect(() => subscribeAppUpdateStatus(setStatus), []);

    useEffect(() => {
        if (!windowObject || !documentObject) {
            return undefined;
        }

        let idleTimerId = null;
        let isIdle = false;

        const markActive = () => {
            if (isIdle) {
                isIdle = false;
                setIsUserIdle(false);
            }

            windowObject.clearTimeout(idleTimerId);
            idleTimerId = windowObject.setTimeout(() => {
                isIdle = true;
                setIsUserIdle(true);
            }, idleAfterMs);
        };

        const handleVisibilityChange = () => {
            setIsPageVisible(readPageVisibility(documentObject));
        };

        markActive();
        ACTIVITY_EVENTS.forEach((eventName) => {
            windowObject.addEventListener(eventName, markActive, { passive: true });
        });
        documentObject.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            windowObject.clearTimeout(idleTimerId);
            ACTIVITY_EVENTS.forEach((eventName) => {
                windowObject.removeEventListener(eventName, markActive);
            });
            documentObject.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [documentObject, idleAfterMs, windowObject]);

    useEffect(() => {
        if (!status || status.isApplying) {
            return;
        }

        const isUpdateWaiting = status.phase === 'ready' || status.phase === 'available';

        if (!isUpdateWaiting) {
            return;
        }

        const decision = resolveAutoUpdateDecision({
            isPageVisible,
            isUserIdle,
            hasBlockingOverlay: hasOpenModalDialog(documentObject),
            hasNavigated,
        });

        if (!decision.canUpdate) {
            return;
        }

        // Keyed on the build being moved to, so a failed apply cannot retry in a
        // loop, and a second deploy while this tab is still open is still taken.
        const updateKey = `${status.phase}:${status.latestVersion ? status.latestVersion.buildId : 'installed-worker'}`;

        if (appliedUpdateKeyRef.current === updateKey) {
            return;
        }

        appliedUpdateKeyRef.current = updateKey;
        applyAppUpdate().catch(console.error);
    }, [documentObject, hasNavigated, isPageVisible, isUserIdle, status]);
}
